import { Component, Input, input, output, signal, computed, ViewChild, inject, effect, AfterViewInit, OnDestroy, OnInit, Injector, ContentChild, TemplateRef
} from '@angular/core';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { GenericTable } from '../generic-table/generic-table';
import { TableHeader } from '../table-header/table-header';
import { TechnicalNamesReportModalComponent } from '../technical-names-report-modal/technical-names-report-modal';
import { GridStateMachine } from './grid-state-machine';
import { FieldMapperHelper } from '../fields/field-mapper.helper';
import { HeaderMode } from '../table-header/enums/HeaderMode';
import { GridRowStatus } from '../enums/gridRowStatus';
import { LocalizationService } from '@abp/ng.core';
import { GridDataManager } from './services/grid-data-manager';
import { ICrudBulkOperationError } from '../interfaces/ICrudError';
import { ZnErrorHandler } from '../services/zn-error-handler';
import { ZnBulkActionResultService } from '../services/zn-bulk-action-result.service';
import { ConfirmationService, Confirmation, ToasterService } from '@abp/ng.theme.shared';
import { EntityPermissions } from './services/entity-permissions';
import { FieldDefinition } from '../fields/field-definition';
import { GridLayout } from '../layout-list/services/grid-layout';
import { PreAccessFilterModal } from '../pre-access-filter/pre-access-filter-modal';
import { PreAccessFilterValue } from '../pre-access-filter/models/pre-access-filter.models';
import { localizeWithFallback } from '../utils/localization-utils';
import { getRequiredFromDependencies } from '../generic-form/utils/field-dependency-utils';
import { isEmptyValue } from '../utils/value-utils';
import { normalizeItemForSave } from './helpers/crud-page-normalization.helper';
import { buildAuditFields } from './helpers/audit-fields.helper';
import { IActionsSchema } from '../zn-other-actions/interfaces/IAction';
import { Column, GridOption } from 'angular-slickgrid';

@Component({
  selector: 'app-crud-page',
  imports: [TableHeader, GenericTable, TechnicalNamesReportModalComponent, PreAccessFilterModal],
  providers: [GridDataManager],
  templateUrl: './crud-page.component.html',
  styleUrl: './crud-page.component.scss',
})
export class CrudPage implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('gridTable') gridTable!: GenericTable;

  private readonly localizationService = inject(LocalizationService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toaster = inject(ToasterService);
  private readonly dataManager = inject(GridDataManager);
  private readonly router = inject(Router);
  private readonly permissionsService = inject(EntityPermissions)
  private readonly injector = inject(Injector);
  private readonly layout = inject(GridLayout);
  private errorHandlerService = inject(ZnErrorHandler);
  private readonly bulkActionResultService = inject(ZnBulkActionResultService);
  private destroy$ = new Subject<void>();

  private readonly auditFields: FieldDefinition[] = buildAuditFields(this.localizationService);

  @Input() loadData?: (query: string) => Observable<any>;

  tableName = input.required<string>();
  fields = input.required<FieldDefinition[]>();
  dataset = input.required<any[]>();
  loading = input<boolean>(false);
  permission = input.required<string>();
  hideCreateButton = input<boolean>(false);
  disableEdit = input<boolean>(false);
  disableEditInline = input<boolean>(false);
  showEditColumn = input<boolean>(true);
  disableCanDelete = input<boolean>(false);
  disableDuplicate = input<boolean>(false);
  showPrefilters = input<boolean>(true);
  rowActionConfig = input<{ title?: string; iconClass?: string } | null>(null);
  extraTableOptions = input<GridOption>({});
  columnDefinitionsOverride = input<Column[] | null>(null);
  rowsWithErrors: ICrudBulkOperationError = { failedRecords: [] };
  actions = input<IActionsSchema | null>(null);
  
  @ContentChild('tableHeaderSubtitle', { read: TemplateRef })
  subtitleTemplate: TemplateRef<unknown> | null = null;

  @ContentChild('tableHeaderActions', { read: TemplateRef })
  actionsTemplate: TemplateRef<unknown> | null = null;

  canCreate = signal(false);
  canEdit = signal(false);
  canEditInline = signal(false);
  canDelete = signal(false);
  

  createRecord = output<void>();
  deleteRecord = output<void>();
  exportInProgress = input(false);
  exportToExcelRecord = output<void>();
  editRow = output<any>();
  rowAction = output<any>();
  saveInMemoryRequested = output<{ mode: HeaderMode; data: any[] }>();
  selectedItemsChange = output<number[]>();

  protected backendServiceProcess?: (query: string) => Observable<any>;

  private readonly stateMachine = new GridStateMachine();
  protected selectedRowIds = signal<number[]>([]);
  private temporaryIdCounter = -1;

  isTechnicalNamesModalOpen = false;
  modalConfig = computed(() => ({
    entityName: this.extractEntityName(this.tableName()),
    localizationKey: this.tableName(),
    fields: this.fields(),
  }));

  isPreAccessModalVisible = signal(false);
  preAccessFiltersState = signal<PreAccessFilterValue[]>([]);
  private readonly syncPreAccessFilters = effect(() => {
    this.preAccessFiltersState.set(this.layout.preAccessFiltersApplied());
  });

  readonly prefilterFields = computed(() =>
    this.fields()
      .filter(f => f.fieldOptions?.prefilter)
      .sort((a, b) =>
        (a.fieldOptions?.prefilter?.order ?? 999) - (b.fieldOptions?.prefilter?.order ?? 999)
      )
  );

  readonly hasPrefilters = computed(() => this.prefilterFields().length > 0);
  readonly shouldWaitForPrefilters = computed(() => this.hasPrefilters() && this.showPrefilters());
  readonly gridFields = computed(() => [...this.fields(), ...this.auditFields]);

  currentMode = computed(() => this.stateMachine.currentState());
  selectedItemsCount = computed(() => this.selectedRowIds().length);
  selectedRowsData = computed(() => {
    const selectedIds = new Set(this.selectedRowIds());

    if (selectedIds.size === 0) {
      return [];
    }

    return this.combinedDataset().filter((item: any) => selectedIds.has(item?.id));
  });
  defaultColumnDefinitions = computed(() =>
    FieldMapperHelper.buildColumnsFromFields(this.gridFields(), this.localizationService, this.injector)
  );
  columnDefinitions = computed(() =>
    this.columnDefinitionsOverride() ?? this.defaultColumnDefinitions()
  );

  combinedDataset = computed(() => {
    const duplicated = this.dataManager.duplicatedRows();
    const cloned = this.dataManager.clonedDataset();
    return [...duplicated, ...cloned];
  });

  hasPendingChanges = computed(() =>
    this.dataManager.duplicatedRows().length > 0 ||
    this.dataManager.editedRows().size > 0
  );

  constructor() {
    this.dataManager.initializeWithDataset(this.dataset);

    effect(() => {
      const count = this.selectedItemsCount();
      this.stateMachine.notify('SELECTION_CLEARED', {
        selectedCount: count,
      });
    });

    effect(() => {
      const data = this.dataManager.clonedDataset();
      const selectedIds = this.selectedRowIds();

      if (selectedIds.length > 0) {
        const datasetIds = new Set(data.map((item: any) => item.id));
        const hasInvalidSelection = selectedIds.some(id => !datasetIds.has(id));

        if (hasInvalidSelection) {
          if (this.gridTable?.grid?.slickGrid) {
            this.gridTable.grid.slickGrid.setSelectedRows([]);
          }

          this.selectedRowIds.set([]);
          this.selectedItemsChange.emit([]);
        }
      }

      this.stateMachine.notify('DATASET_CHANGED', {
        dataset: data,
        selectedIds: selectedIds,
      });
    });

    effect(() => {
      const duplicates = this.dataManager.duplicatedRows();
      this.stateMachine.notify('DUPLICATES_CHANGED', {
        hasDuplicates: duplicates.length > 0,
      });
    });
  }

  ngOnInit(): void {
    this.permissionsService.loadPermissions(this.permission());
    // If hideCreateButton, disableEdit, disableEditInline, disableCanDelete are true, force to false regardless of permissions
    this.canCreate.set(this.hideCreateButton() ? false : this.permissionsService.canCreate);
    this.canEdit.set(this.disableEdit() ? false : this.permissionsService.canEdit);
    this.canEditInline.set(
      this.disableEdit() ||
      this.disableEditInline() ||
      !this.permissionsService.canEdit
        ? false
        : this.permissionsService.canEditInline
    );
    this.canDelete.set(this.disableCanDelete() ? false : this.permissionsService.canDelete);

    this.errorHandlerService.errorData$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.rowsWithErrors = data;
    });

    this.bulkActionResultService.errorData$.pipe(takeUntil(this.destroy$)).subscribe(data => {
      this.rowsWithErrors = data;
    });

    this.bulkActionResultService.completed$.pipe(takeUntil(this.destroy$)).subscribe(result => {
      this.clearSelection();

      if (result.hasProcessedRecords) {
        this.gridTable?.grid?.extensionService?.refreshBackendDataset();
      }
    });

    if (this.loadData) {
      this.backendServiceProcess = this.loadData;
    }

    this.layout.setSuppressInitialLayoutQuery(this.shouldWaitForPrefilters());

    // Show pre-access modal if there are prefilters configured
    if (this.shouldWaitForPrefilters()) {
      this.isPreAccessModalVisible.set(true);
    }
  }

  ngAfterViewInit(): void {
    this.stateMachine.setActions({
      cleanAllState: () => this.dataManager.cleanAll(this.dataset()),
      cleanDuplicatedRows: () => this.dataManager.cleanDuplicated(),
      cleanEditedRows: () => this.dataManager.cleanEdited(),
      enableCheckboxColumn: () =>
        this.gridTable?.toggleColumn(true, '_checkbox_selector'),
      disableCheckboxColumn: () =>
        this.gridTable?.toggleColumn(false, '_checkbox_selector'),
      enableEditColumn: () => this.gridTable?.toggleColumn(true, '_edit'),
      disableEditColumn: () => this.gridTable?.toggleColumn(false, '_edit'),
      disableInlineEditing: () => this.gridTable?.disableInlineEditing(),
      enableInlineEditing: () => this.gridTable?.enableInlineEditing(),
      refreshGrid: () => this.gridTable?.refresh(),
      blockCellEdit: () => this.gridTable?.blockCellEdit(),
      activateCell: (row, cell) => this.gridTable?.activateCell(row, cell),
      enableSingleClickEdit: () => this.gridTable?.enableSingleClickEdit(),
      applyBoldToDuplicatedRows: () => this.applyBoldToDuplicatedRows(),
      applyBoldToEditedRows: () => this.applyBoldToEditedRows(),
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCreateRecord(): void {
    this.createRecord.emit();
  }

  onDeleteRecord(): void {
    this.deleteRecord.emit();
  }

  onExportToExcelRecord(): void {
    this.exportToExcelRecord.emit();
  }

  onTechnicalNamesReport(): void {
    this.isTechnicalNamesModalOpen = true;
  }

  onDuplicateRecord(): void {
    if (!this.gridTable?.grid) {
      return;
    }

    const selectedData = this.gridTable.grid.gridService.getSelectedRowsDataItem();

    if (selectedData.length === 0) {
      return;
    }

    const duplicates = selectedData.map(row => ({
      ...row,
      id: this.generateTempId(),
      rowStatus: GridRowStatus.Duplicated,
    }));

    this.dataManager.addDuplicated(duplicates);
    this.gridTable.grid.slickGrid.setSelectedRows([]);

    this.stateMachine.transition('DUPLICATE');
  }

  onSummaryRecord(): void {
    this.gridTable.showSummary()
  }

  clearSelection(): void {
    this.gridTable?.grid?.slickGrid?.setSelectedRows([]);
    this.onRowSelectionChange([]);
  }

  onSaveRecord(): void {
    const editorLock = this.gridTable?.grid?.slickGrid?.getEditorLock?.();
    if (editorLock) {
      const committed = editorLock.commitCurrentEdit();
      if (!committed) {
        return;
      }
    }

    const data = this.stateMachine.getDataToSave({
      duplicatedRows: this.dataManager.getDuplicatedRowsArray(),
      editedRows: this.dataManager.editedRows(),
    });

    if (data.length === 0) {
      return;
    }

    const fields = this.fields() || [];
    if (fields.length > 0) {
      const missingField = this.findFirstMissingRequiredField(fields, data);
      if (missingField) {
        const label = localizeWithFallback(this.localizationService, missingField.label || missingField.key);
        const requiredMessage = localizeWithFallback(
          this.localizationService,
          'LanguageService::DefaultErrorRequired'
        );
        this.toaster.error(`${label}: ${requiredMessage}`, null, { life: 5000 });
        return;
      }
    }

    this.saveInMemoryRequested.emit({ mode: this.currentMode(), data });
  }

  onCancelRecord(): void {
    if (!this.hasPendingChanges()) {
      return;
    }

    this.showCancelConfirmation().subscribe((status) => {
      if (status === Confirmation.Status.confirm) {
        this.cancelChanges();
      }
    });
  }

  private showCancelConfirmation() {

    return this.confirmationService.show(
      localizeWithFallback(this.localizationService, 'LanguageService::ConfirmCancelChanges'),
      localizeWithFallback(this.localizationService, 'LanguageService::ConfirmCancelChangesTitle'),
      'warning',
      {
        iconTemplate: '<i class="bi bi-exclamation-octagon-fill"></i>',
        yesText: localizeWithFallback(this.localizationService, 'LanguageService::Option:Yes'),
        cancelText: localizeWithFallback(this.localizationService, 'LanguageService::Option:No')
      }
    );
  }

  private cancelChanges(): void {
    this.clearUnsavedRowStyles();
    this.stateMachine.transition('CANCEL');
  }

  onGridReady(grid: any): void {
    this.layout.initialize(
      grid,
      this.modalConfig().entityName,
      this.modalConfig().localizationKey,
      this.permissionsService.create,
      this.gridFields()
    );
  }

  onEditRow(row: any): void {
    this.editRow.emit(row);
  }

  onRowAction(row: any): void {
    this.rowAction.emit(row);
  }

  onCellEdited(_data: any): void {}

  onRowSelectionChange(rows: number[]): void {
    this.selectedRowIds.set(rows);
    this.selectedItemsChange.emit(rows);

    this.stateMachine.notify('ROW_SELECTION_CHANGED', {
      selectedCount: rows.length,
      hasEdits: this.dataManager.editedRows().size > 0,
      hasDuplicates: this.dataManager.duplicatedRows().length > 0,
    });
  }

  onBeforeEditCell(data: any): void {
    const id = data.args.item.id;

    if (!this.dataManager.originalRows().has(id)) {
      const originalItem = this.dataManager.clonedDataset().find(row => row.id === id);

      if (originalItem) {
        this.dataManager.saveOriginal(id, originalItem);
      }
    }
    this.stateMachine.notify('CELL_EDIT_ATTEMPT', {
      itemId: data.args.item.id,
    });
  }

  onTriggerEditMode(data: any): void {
    this.stateMachine.notify('EDIT_MODE_REQUESTED', {
      row: data.args?.row,
      cell: data.args?.cell,
    });
  }

  onCellChange(data: any): void {
    const id = data.args.item.id;
    const rowStatus = id < 0 ? GridRowStatus.Duplicated : GridRowStatus.Modified;
    const fields = this.fields() || [];
    const normalizedItem = fields.length > 0
      ? normalizeItemForSave(data.args.item, fields)
      : data.args.item;

    if (normalizedItem !== data.args.item) {
      Object.assign(data.args.item, normalizedItem);
    }

    this.dataManager.saveEdited(id, { ...normalizedItem, rowStatus });
    
    this.stateMachine.notify('CELL_EDITED', {
      itemId: id,
      hasEdits: this.dataManager.editedRows().size > 0,
    });
  }

  onSaveCompleted(): void {
    this.clearUnsavedRowStyles();
    this.stateMachine.transition('SAVE');
  }

  removeDuplicatedRow(id: number): void {
    this.dataManager.removeDuplicatedRow(id);
  }

  private clearUnsavedRowStyles(): void {
    const grid = this.gridTable?.grid?.slickGrid;
    if (!grid) return;

    const dataView = grid.getData();
    const rowCount = dataView.getLength();

    for (let i = 0; i < rowCount; i++) {
      const rowElm = grid.getCellNode(i, 0)?.parentElement;
      if (rowElm?.classList.contains('row-unsaved')) {
        rowElm.classList.remove('row-unsaved');
      }
    }
  }

  private applyBoldToDuplicatedRows(): void {
    setTimeout(() => {
      const grid = this.gridTable?.grid?.slickGrid;
      const dataView = grid?.getData();
      if (!grid || !dataView) return;

      for (let i = 0; i < dataView.getLength(); i++) {
        const item = dataView.getItem(i);
        if (item && item.id < 0) {  // Negative IDs are duplicated rows
          const rowElm = grid.getCellNode(i, 0)?.parentElement;
          if (rowElm && !rowElm.classList.contains('row-unsaved')) {
            rowElm.classList.add('row-unsaved');
          }
        }
      }
    }, 0);
  }

  private applyBoldToEditedRows(): void {
    const grid = this.gridTable?.grid?.slickGrid;
    const dataView = grid?.getData();
    if (!grid || !dataView) return;

    for (let i = 0; i < dataView.getLength(); i++) {
      const item = dataView.getItem(i);
      if (item && this.dataManager.editedRows().has(item.id)) {
        const rowElm = grid.getCellNode(i, 0)?.parentElement;
        if (rowElm && !rowElm.classList.contains('row-unsaved')) {
          rowElm.classList.add('row-unsaved');
        }
      }
    }
  }

  private findFirstMissingRequiredField(
    fields: FieldDefinition[],
    data: any[]
  ): FieldDefinition | null {
    for (const record of data) {
      for (const field of fields) {
        const isRequired = this.resolveRequiredForRow(field, record ?? {});
        if (!isRequired) {
          continue;
        }
        const val = record?.[field.key];
        if (isEmptyValue(val)) {
          return field;
        }
      }
    }
    return null;
  }

  private resolveRequiredForRow(field: FieldDefinition, values: Record<string, any>): boolean {
    const fieldOptions = field.fieldOptions;
    if (!fieldOptions || fieldOptions.hidden) {
      return false;
    }

    const baseRequired = !!fieldOptions.required;
    const rules = fieldOptions.dependencies ?? [];
    if (rules.length === 0) {
      return baseRequired;
    }

    return getRequiredFromDependencies(rules, values, baseRequired);
  }

  private generateTempId(): number {
      return this.temporaryIdCounter--;
  }

  private extractEntityName(localizationKey: string): string {
    const parts = localizationKey.split('.');
    return parts[parts.length - 1] || localizationKey;
  }

  // Pre-access filter handlers
  onPreAccessFiltersApplied(filters: PreAccessFilterValue[]): void {
    this.preAccessFiltersState.set(filters);
    this.layout.setPreAccessFilters(filters);
  }

  onPreAccessFiltersClosed(): void {
    this.router.navigateByUrl('/');
  }
}
